import React, { useState, useEffect } from 'react';
import { subscribeRoom, leaveRoom, rematch } from '../shared/roomTx';
import { FL_ROOM_STATUS, FL_GAME_MODE } from '../shared/fiveLeague';
import { CyberBackground, NeonText, CyberButton, GlowingBorder } from '../components/CyberEffects';
import './ChainResult.css';

function ChainResult({ navigate, playerId, roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rematchLoading, setRematchLoading] = useState(false);
  const [error, setError] = useState('');

  // ルーム購読
  useEffect(() => {
    if (!roomId) {
      navigate('home');
      return;
    }

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      if (!data) {
        navigate('home');
        return;
      }

      // 再戦でWAITINGに戻ったら待機画面へ
      if (data.status === FL_ROOM_STATUS.WAITING) {
        navigate('wait', { roomId });
        return;
      }

      setRoom(data);
    });

    return () => unsub();
  }, [roomId, navigate]);

  const handlePlayAgain = async () => {
    if (rematchLoading) return;

    setRematchLoading(true);
    setError('');

    try {
      const result = await rematch(roomId, playerId);
      if (!result.ok) {
        if (result.reason === 'not-host') {
          setError('ホストのみが再戦を開始できます');
        } else {
          setError('再戦の開始に失敗しました');
        }
      }
      // 成功したらsubscribeRoomが待機画面へ遷移させる
    } catch (e) {
      console.error('[Rematch] Error:', e);
      setError('エラーが発生しました');
    } finally {
      setRematchLoading(false);
    }
  };

  const handleLeave = async () => {
    await leaveRoom(roomId, playerId);
    navigate('home');
  };

  if (loading) {
    return (
      <div className="chain-result-container">
        <CyberBackground showParticles={true} />
        <div className="loading-spinner">読み込み中...</div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="chain-result-container">
        <CyberBackground showParticles={true} />
        <p className="error-message">ルームが見つかりません</p>
        <button className="btn-secondary" onClick={() => navigate('home')}>
          ホームに戻る
        </button>
      </div>
    );
  }

  const game = room.game;
  if (!game) {
    return (
      <div className="chain-result-container">
        <CyberBackground showParticles={true} />
        <p className="error-message">ゲームデータがありません</p>
        <button className="btn-secondary" onClick={() => navigate('home')}>
          ホームに戻る
        </button>
      </div>
    );
  }

  const isPerfect = game.correctCount === game.answerCount;
  const controllers = room.players?.filter(p => p.role !== 'display') || [];

  return (
    <div className="chain-result-container animate-fade-in">
      <CyberBackground showParticles={true} />

      <div className="chain-result-content">
        {/* 結果ヘッダー */}
        <div className="result-header">
          <div className={`result-icon ${isPerfect ? 'perfect' : 'gameover'}`}>
            {isPerfect ? '🎉' : '💥'}
          </div>
          <h1 className="result-title">
            <NeonText color={isPerfect ? 'accent' : 'pink'} size="xl">
              {isPerfect ? 'PERFECT!' : 'GAME OVER'}
            </NeonText>
          </h1>
        </div>

        {/* スコア */}
        <div className="result-score">
          <GlowingBorder color={isPerfect ? 'accent' : 'blue'} className="score-box">
            <span className="score-current">{game.correctCount}</span>
            <span className="score-separator">/</span>
            <span className="score-total">{game.answerCount}</span>
            <span className="score-label">正解</span>
          </GlowingBorder>
        </div>

        {/* 回答リスト */}
        <div className="answers-section">
          <h2 className="answers-title">
            <NeonText color="blue" size="sm">回答一覧</NeonText>
          </h2>
          <div className="answers-list">
            {game.submittedAnswers?.map((answer, i) => {
              const player = room.players.find(p =>
                room.playerOrder[answer.playerSlot] === p.playerId
              );
              return (
                <div key={i} className="answer-item">
                  <span className="answer-num">{i + 1}</span>
                  <span className="answer-text">{answer.text}</span>
                  <span className="answer-player">{player?.nickname || '???'}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 残りの正解（タイムアウト時） */}
        {!isPerfect && game.answersForDisplay && (
          <div className="remaining-section">
            <h2 className="remaining-title">
              <NeonText color="pink" size="sm">残りの答え</NeonText>
            </h2>
            <div className="remaining-list">
              {game.answersForDisplay
                .filter(ans => !game.submittedAnswers?.some(s => s.reading === ans.reading))
                .slice(0, game.answerCount - game.correctCount)
                .map((answer, i) => (
                  <div key={i} className="remaining-item">
                    {answer.text}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* エラーメッセージ */}
        {error && (
          <p className="error-message" style={{ color: '#ff6b6b', marginBottom: '1rem' }}>
            {error}
          </p>
        )}

        {/* アクションボタン */}
        <div className="result-actions">
          <CyberButton
            variant="primary"
            className="play-again-btn"
            onClick={handlePlayAgain}
            disabled={rematchLoading}
          >
            {rematchLoading ? '準備中...' : 'もう一度遊ぶ'}
          </CyberButton>
          <button
            className="cyber-btn-secondary leave-btn"
            onClick={handleLeave}
          >
            終了する
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChainResult;
